import { useEffect, useMemo, useState } from 'react'
import {
  Badge,
  Box,
  Button,
  HStack,
  IconButton,
  Spinner,
  Tag,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import { StatTile } from '../../shared/viz'
import { toaster } from '@/components/ui/toaster'
import { FiCalendar, FiChevronLeft, FiChevronRight, FiPlus, FiSave, FiZap } from 'react-icons/fi'
import {
  DndContext, DragOverlay, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core'
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import { useLobbyDayEvent } from '../../../hooks/useLobbyDay'
import { useLobbyScheduleEditor } from '../../../hooks/useLobbyScheduleEditor'
import { useAuth } from '../../../hooks/useAuth'
import { PacketPreviewDrawer } from '../PacketPreviewDrawer'
import { AttendeePool } from './AttendeePool'
import { AutoBuildDialog } from './AutoBuildDialog'
import { ScheduleAgenda } from './ScheduleAgenda'
import { ConflictSummaryBar } from './ConflictSummaryBar'
import { DraftRecoveryBanner } from './DraftRecoveryBanner'
import { ScheduleConflictBanner } from './ScheduleConflictBanner'
import { LegislatorCoveragePanel } from './LegislatorCoveragePanel'
import { describeConflicts } from './conflictSummary'
import { MeetingDetailSheet } from './MeetingDetailSheet'
import { AttendeePickerSheet } from './AttendeePickerSheet'
import { meetingValidityColor } from './LobbyScheduleAgendaEditor'
import { clampEndToDay } from './timelineLayout'
import { schedulerCollisionDetection } from './schedulerCollision'
import { CreateMeetingPopover } from './CreateMeetingPopover'
import type { CreateMeetingContext } from './CreateMeetingPopover'
import {
  DAY_START_MINUTES, constituentsByLegislator, findConflicts, minutesToTime, timeToMinutes,
} from './lobbyScheduleUtils'
import type {
  MeetingValidity, ScheduleMeetingDto,
} from '../../../types/lobbyDay'

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + 'T00:00:00')
  d.setDate(d.getDate() + days)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function formatDateLabel(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
}

const VALIDITY_ORDER: { key: MeetingValidity; label: string; color: string }[] = [
  { key: 'Valid', label: 'valid', color: 'green' },
  { key: 'Unverified', label: 'unverified', color: 'yellow' },
  { key: 'NoConstituent', label: 'no constituent', color: 'red' },
  { key: 'Empty', label: 'empty', color: 'gray' },
]

const RAIL_STICKY_TOP_PX = 96   // clears the sticky stat/action header
const RAIL_CHROME_OFFSET_PX = 120

/** Current drag payload (mirrors ItineraryBuilder.tsx's `activeDrag` state, adapted to this
 *  module's two draggable kinds). */
type ActiveDrag =
  | { type: 'pool-attendee'; attendeeId: string }
  | { type: 'assignment'; attendeeId: string; assignmentId: string; fromMeetingId: string }
  | { type: 'meeting-card'; meetingId: string }

interface LobbySchedulerProps {
  eventId: string
  onEditorState?: (s: {
    isDirty: boolean
    isSaving: boolean
    save: (opts?: { onSuccess?: () => void; onError?: () => void }) => void
    discardDraft: () => void
  }) => void
}

/** The scheduler grid: legislators × time slots for the selected day, plus the attendee pool.
 *  Drag-and-drop editing + save (Task 13), mirroring ItineraryBuilder's pending-local-state,
 *  isDirty, save, and beforeunload guard patterns. Day switching itself has no guard: localMeetings
 *  covers every day, so switching days never discards an edit. */
export function LobbyScheduler({ eventId, onEditorState }: LobbySchedulerProps) {
  const editor = useLobbyScheduleEditor(eventId)
  const {
    schedule, meetings: localMeetings, selectedDate, setSelectedDate,
    isLoading, error, isDirty, isSaving, save, createMeeting, stageSuggestions,
    autoBuildTargetCount, autoBuildItinerary,
    assign, unassign, move, deleteMeeting, reschedule, resize,
    setMeetingAsk, setMeetingRoom, setMeetingStatus, setAttendance,
    setMeetingWithWhom, setRole, setMyStaffTag,
    recoverableDraft, restoreDraft, discardDraft,
    conflict, reloadLatest,
  } = editor
  const { user } = useAuth()
  const { data: event } = useLobbyDayEvent(eventId)
  const autoBuildDisclosure = useDisclosure()

  useEffect(() => {
    onEditorState?.({ isDirty, isSaving, save, discardDraft })
  }, [isDirty, isSaving, save, discardDraft, onEditorState])
  useEffect(() => () => onEditorState?.({ isDirty: false, isSaving: false, save: () => {}, discardDraft: () => {} }), [onEditorState])

  const [packetMeetingId, setPacketMeetingId] = useState<string | null>(null)
  const [activeDrag, setActiveDrag] = useState<ActiveDrag | null>(null)
  const [popover, setPopover] = useState<CreateMeetingContext | null>(null)
  type HoverContext =
    | { source: 'attendee'; attendeeId: string }
    | { source: 'card'; legislatorId: string }
    | null
  const [hover, setHover] = useState<HoverContext>(null)
  const setHoverAttendee = (attendeeId: string | null) =>
    setHover(attendeeId ? { source: 'attendee', attendeeId } : null)
  const setHoverCard = (legislatorId: string | null) =>
    setHover(legislatorId ? { source: 'card', legislatorId } : null)
  const [openMeetingId, setOpenMeetingId] = useState<string | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)

  const legislatorById = useMemo(
    () => new Map((schedule?.legislators ?? []).map((l) => [l.legislatorId, l] as const)), [schedule])
  const attendeeById = useMemo(
    () => new Map((schedule?.attendees ?? []).map((a) => [a.attendeeId, a] as const)), [schedule])
  const colorForMeeting = (m: ScheduleMeetingDto) =>
    meetingValidityColor(m, legislatorById, attendeeById).color

  const openMeeting = openMeetingId ? localMeetings.find((m) => m.id === openMeetingId) ?? null : null
  const openLegislator = openMeeting ? legislatorById.get(openMeeting.legislatorId) : undefined
  const assignedIds = useMemo(
    () => new Set(openMeeting?.assignments.map((a) => a.attendeeId) ?? []), [openMeeting])

  const conflicts = useMemo(() => findConflicts(localMeetings), [localMeetings])

  const conflictChips = useMemo(() => {
    const map = new Map<string, Set<string>>()
    for (const c of conflicts) {
      for (const mid of [c.meetingIdA, c.meetingIdB]) {
        if (!map.has(mid)) map.set(mid, new Set())
        map.get(mid)!.add(c.attendeeId)
      }
    }
    return map
  }, [conflicts])

  const attendeeNameById = useMemo(
    () => new Map((schedule?.attendees ?? []).map((a) => [a.attendeeId, a.fullName] as const)), [schedule])
  const conflictRows = useMemo(
    () => describeConflicts(conflicts, localMeetings, legislatorById, attendeeNameById),
    [conflicts, localMeetings, legislatorById, attendeeNameById])

  const [flashMeetingIds, setFlashMeetingIds] = useState<Set<string>>(new Set())
  const flashConflict = (a: string, b: string) => {
    // A conflict is two time-overlapping meetings, so both sit on the same day. If that day isn't
    // the one on screen, switch to it first — otherwise the agenda only renders the active day's
    // meetings and clicking a summary row for another day flashes nothing visible.
    const day = localMeetings.find((m) => m.id === a || m.id === b)?.meetingDate
    if (day) setSelectedDate(day)
    setFlashMeetingIds(new Set([a, b]))
    window.setTimeout(() => setFlashMeetingIds(new Set()), 1500)
  }

  const validityCounts = useMemo(() => {
    const counts: Record<MeetingValidity, number> = { Empty: 0, Valid: 0, Unverified: 0, NoConstituent: 0 }
    if (!schedule) return counts
    for (const m of localMeetings) counts[meetingValidityColor(m, legislatorById, attendeeById).validity]++
    return counts
  }, [localMeetings, schedule, legislatorById, attendeeById])

  // DnD sensors (mirrors ItineraryBuilder.tsx:131-134)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const constituentsByLeg = useMemo(
    () => constituentsByLegislator(schedule?.attendees ?? []), [schedule])

  // Attendee tinting (pool + card chips): hovered attendee, or the hovered card's constituents.
  const highlightAttendeeIds = useMemo<Set<string> | null>(() => {
    if (!hover) return null
    if (hover.source === 'attendee') return new Set([hover.attendeeId])
    return constituentsByLeg.get(hover.legislatorId) ?? null
  }, [hover, constituentsByLeg])

  // Card glow: the hovered card's own legislator, or the hovered attendee's legislators.
  const highlightLegislatorIds = useMemo<Set<string> | null>(() => {
    if (!hover) return null
    if (hover.source === 'card') return new Set([hover.legislatorId])
    return new Set(attendeeById.get(hover.attendeeId)?.constituentOfLegislatorIds ?? [])
  }, [hover, attendeeById])

  const handleDragStart = (e: DragStartEvent) => {
    const data = e.active.data.current
    if (data?.type === 'pool-attendee') {
      setActiveDrag({ type: 'pool-attendee', attendeeId: data.attendeeId })
    } else if (data?.type === 'assignment') {
      setActiveDrag({
        type: 'assignment',
        attendeeId: data.attendeeId,
        assignmentId: data.assignmentId,
        fromMeetingId: data.fromMeetingId,
      })
    } else if (data?.type === 'meeting-card') {
      setActiveDrag({ type: 'meeting-card', meetingId: data.meetingId })
    }
  }

  const handleDragEnd = (e: DragEndEvent) => {
    setActiveDrag(null)
    const { active, over } = e
    if (!over || !schedule) return
    const a = active.data.current as ActiveDrag | undefined
    const o = over.data.current as
      | { type: 'meeting'; meetingId: string }
      | { type: 'slot'; slotTime: string }
      | { type: 'pool' }
      | undefined
    if (!a || !o) return

    // --- rescheduling a whole meeting card ---
    if (a.type === 'meeting-card') {
      if (o.type === 'slot') {
        const m = localMeetings.find((x) => x.id === a.meetingId)
        if (m) {
          const durationMin = (timeToMinutes(m.endTime) - timeToMinutes(m.startTime)) || 30
          reschedule(a.meetingId, o.slotTime, clampEndToDay(o.slotTime, durationMin))
        }
      }
      return
    }

    // --- pool attendee ---
    if (a.type === 'pool-attendee') {
      if (o.type === 'meeting') {
        assign(o.meetingId, a.attendeeId)
      } else if (o.type === 'slot') {
        // pool → empty slot: open the create-meeting popover pre-filled
        setPopover({ legislatorId: schedule.legislators[0]?.legislatorId ?? '', slotTime: o.slotTime, pendingAttendeeId: a.attendeeId, pick: true })
      }
      // pool → pool (or nowhere): no-op
      return
    }

    // --- existing assignment chip ---
    if (a.type === 'assignment') {
      if (o.type === 'meeting') {
        move(a.assignmentId, a.fromMeetingId, o.meetingId, a.attendeeId)
      } else if (o.type === 'pool') {
        unassign(a.fromMeetingId, a.assignmentId)
      }
      // assignment → empty slot (or nowhere): no-op
      return
    }
  }

  const openCreateForLegislator = (legislatorId: string) =>
    setPopover({ legislatorId, slotTime: minutesToTime(DAY_START_MINUTES), pick: true })

  if (isLoading) return <VStack py={12}><Spinner size="lg" color="accent" /></VStack>
  if (!!error || !schedule) return <Text color="red.500">Failed to load schedule.</Text>

  const showDaySwitcher = !!schedule.endDate && schedule.endDate !== schedule.startDate
  const date = selectedDate ?? schedule.startDate
  const activeAttendee = activeDrag && activeDrag.type !== 'meeting-card'
    ? schedule.attendees.find((a) => a.attendeeId === activeDrag.attendeeId)
    : undefined

  return (
    <Box overflowX="clip">
      {showDaySwitcher && (
        <HStack mb={3} gap={2}>
          <IconButton
            aria-label="Previous day"
            size="sm"
            variant="outline"
            disabled={date <= schedule.startDate}
            onClick={() => setSelectedDate(addDays(date, -1))}><FiChevronLeft /></IconButton>
          <Text fontWeight="medium" minW="160px" textAlign="center">{formatDateLabel(date)}</Text>
          <IconButton
            aria-label="Next day"
            size="sm"
            variant="outline"
            disabled={date >= schedule.endDate!}
            onClick={() => setSelectedDate(addDays(date, 1))}><FiChevronRight /></IconButton>
        </HStack>
      )}

      <HStack
        mb={4} p={3} bg="surface" borderRadius="lg" shadow="md" justify="space-between" wrap="wrap" gap={4}
        position="sticky" top={0} zIndex={10}
      >
        <HStack gap={6} wrap="wrap">
          <StatTile label="Meetings" value={localMeetings.length} />
          <HStack gap={2}>
            {VALIDITY_ORDER.map(({ key, label, color }) => (
              <Badge key={key} colorPalette={color}>{validityCounts[key]} {label}</Badge>
            ))}
          </HStack>
        </HStack>
        <HStack gap={3} wrap="wrap" justify="flex-end">
          {isDirty && <Text fontSize="sm" color="orange.500">Unsaved changes</Text>}
          <Button
            size="sm"
            variant="outline"
            colorPalette="brand"
            onClick={autoBuildDisclosure.onOpen}><FiCalendar />Auto-build
                      </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={localMeetings.length === 0}
            onClick={() => {
              const { stagedCount, meetingsAffected } = stageSuggestions()
              toaster.create(
                stagedCount > 0
                  ? {
                      title: `Staged ${stagedCount} assignment${stagedCount === 1 ? '' : 's'} across ${meetingsAffected} meeting${meetingsAffected === 1 ? '' : 's'}`,
                      type: 'success', duration: 2500,
                    }
                  : { title: 'No new assignments to suggest', type: 'info', duration: 2500 },
              )
            }}><FiZap />Suggest
                      </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={schedule.legislators.length === 0}
            onClick={() => setPopover({
              legislatorId: schedule.legislators[0]?.legislatorId ?? '',
              slotTime: minutesToTime(DAY_START_MINUTES),
              pick: true,
            })}><FiPlus />Meeting
                      </Button>
          <Button
            size="sm"
            colorPalette="brand"
            onClick={() => save({
              onSuccess: () => toaster.create({ title: 'Schedule saved', type: 'success', duration: 2000 }),
              onError: () => toaster.create({ title: 'Failed to save schedule', type: 'error', duration: 3000 }),
            })}
            loading={isSaving}
            disabled={!isDirty || conflict}><FiSave />Save
                      </Button>
        </HStack>
      </HStack>

      {conflict && <ScheduleConflictBanner onReload={reloadLatest} />}

      {recoverableDraft && (
        <DraftRecoveryBanner onRestore={restoreDraft} onDiscard={discardDraft} />
      )}

      <ConflictSummaryBar rows={conflictRows} onFlash={flashConflict} />

      <DndContext
        sensors={sensors}
        collisionDetection={schedulerCollisionDetection}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <HStack align="flex-start" gap={4} minW={0}>
          <VStack
            align="stretch" gap={3} flexShrink={0}
            position="sticky" top={`${RAIL_STICKY_TOP_PX}px`}
            maxH={`calc(100vh - ${RAIL_CHROME_OFFSET_PX}px)`} overflowY="auto"
          >
            <LegislatorCoveragePanel
              eventId={schedule.eventId}
              meetings={localMeetings}
              onAddMeeting={openCreateForLegislator}
            />
            <AttendeePool
              attendees={schedule.attendees}
              meetings={localMeetings}
              activeAttendeeId={activeDrag?.type === 'pool-attendee' ? activeDrag.attendeeId : null}
              highlightAttendeeIds={highlightAttendeeIds}
              onHoverAttendee={setHoverAttendee}
            />
          </VStack>
          <ScheduleAgenda
            legislators={schedule.legislators}
            meetings={localMeetings}
            date={date}
            colorForMeeting={colorForMeeting}
            conflictChips={conflictChips}
            flashMeetingIds={flashMeetingIds}
            highlightAttendeeIds={highlightAttendeeIds}
            highlightLegislatorIds={highlightLegislatorIds}
            onOpenMeeting={setOpenMeetingId}
            onDeleteMeeting={deleteMeeting}
            onResizeMeeting={resize}
            onHoverAttendee={setHoverAttendee}
            onHoverCard={setHoverCard}
          />
        </HStack>
        <DragOverlay>
          {activeDrag && activeAttendee && (
            activeDrag.type === 'pool-attendee' ? (
              <Box
                bg="surface" borderWidth="1px" borderColor="accent" borderRadius="md"
                p={2} shadow="lg" fontSize="sm" fontWeight="medium"
                transform="rotate(-1.5deg)"
              >
                {activeAttendee.fullName}
              </Box>
            ) : (
              <Tag.Root size="sm" colorPalette="gray" shadow="lg" transform="rotate(-1.5deg)">
                <Tag.Label>{activeAttendee.fullName}</Tag.Label>
              </Tag.Root>
            )
          )}
        </DragOverlay>
      </DndContext>

      <CreateMeetingPopover
        context={popover}
        asks={event?.asks ?? []}
        legislators={schedule.legislators}
        attendees={schedule.attendees}
        currentDay={date}
        onCreate={(meeting) => { createMeeting(meeting); setPopover(null) }}
        onClose={() => setPopover(null)}
      />

      <PacketPreviewDrawer eventId={eventId} meetingId={packetMeetingId} onClose={() => setPacketMeetingId(null)} />

      <MeetingDetailSheet
        isOpen={openMeeting !== null}
        onClose={() => setOpenMeetingId(null)}
        meeting={openMeeting}
        legislator={openLegislator}
        allMeetings={localMeetings}
        legislatorById={legislatorById}
        onUnassign={(assignmentId) => { if (openMeeting) unassign(openMeeting.id, assignmentId) }}
        onMove={(assignmentId, toMeetingId, attendeeId) => { if (openMeeting) move(assignmentId, openMeeting.id, toMeetingId, attendeeId) }}
        onAddAttendee={() => setPickerOpen(true)}
        onDelete={(meetingId) => { deleteMeeting(meetingId); setOpenMeetingId(null) }}
        onReschedule={reschedule}
        onOpenPacket={(meetingId) => { setPacketMeetingId(meetingId); setOpenMeetingId(null) }}
        asks={event?.asks ?? []}
        onSetAsk={(meetingId, askId) => setMeetingAsk(meetingId, askId)}
        onSetRoom={(meetingId, room) => setMeetingRoom(meetingId, room)}
        onSetStatus={(meetingId, status) => setMeetingStatus(meetingId, status)}
        onSetAttendance={(meetingId, assignmentId, status) => setAttendance(meetingId, assignmentId, status)}
        onSetWithWhom={setMeetingWithWhom}
        onSetRole={setRole}
        currentUserId={user?.id}
        onSetMyStaffTag={setMyStaffTag}
      />
      <AttendeePickerSheet
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        attendees={schedule.attendees}
        legislator={openLegislator}
        assignedAttendeeIds={assignedIds}
        onSelect={(attendeeId) => { if (openMeeting) assign(openMeeting.id, attendeeId) }}
      />

      <AutoBuildDialog
        isOpen={autoBuildDisclosure.open}
        onClose={autoBuildDisclosure.onClose}
        targetCount={autoBuildTargetCount}
        onBuild={(durationMin) => {
          const s = autoBuildItinerary(durationMin)
          autoBuildDisclosure.onClose()
          toaster.create({
            title: `Created ${s.createdCount} meeting${s.createdCount === 1 ? '' : 's'}`,
            description: `${s.assignedCount} constituent${s.assignedCount === 1 ? '' : 's'} assigned · ${s.gapCount} gap${s.gapCount === 1 ? '' : 's'}${s.unplacedCount ? ` · ${s.unplacedCount} didn't fit` : ''}`,
            type: s.createdCount > 0 ? 'success' : 'info',
            duration: 4000,
          })
        }}
      />
    </Box>
  );
}
